// ----------------------------------------------------------------------------
// pianoroll — Full-fledged Piano roll panel for DAWSEUR
//
// Features:
//   - Canvas-based note grid with MIDI note range C-1 to C9 (0-120)
//   - Left vertical keyboard (click to preview audio)
//   - Smooth note dragging (move + resize from both edges)
//   - Animated playhead during preview
//   - ADSR editor with canvas mouse interaction (no sliders)
//   - Waveform overlay on ADSR for visual reference
//   - Sample tab with waveform + loop point editing
//
// Note representation: { midi, startStep, lengthSteps, velocity }
//   midi = absolute MIDI note number (0-127), 60 = C4 = original pitch
//   startStep = step index (16th notes)
//   lengthSteps = duration in steps
//   velocity = 0-1
// ----------------------------------------------------------------------------

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const MIDI_MIN = 0;       // C-1
const MIDI_MAX = 120;     // C9
const REFERENCE_MIDI = 60; // C4 = original sample pitch
const STEPS_PER_BEAT = 4;
const DEFAULT_BEATS = 4;
const BLACK_KEYS = [1, 3, 6, 8, 10];

function midiToNoteName(midi) {
   const noteIdx = ((midi % 12) + 12) % 12;
   const octave = Math.floor(midi / 12) - 1;
   return NOTE_NAMES[noteIdx] + octave;
}

function semitoneToPlaybackRate(midi) {
   return Math.pow(2, (midi - REFERENCE_MIDI) / 12);
}

function createPianoRoll(opts = {}) {
   const container = document.getElementById('lw-pianoroll-body');
   if (!container) return null;

   const state = {
      patterns: [],
      activePatternIdx: 0,
      activeTab: 'sample',
      getBucketItems: opts.getBucketItems || (() => []),
      onPatternDrag: opts.onPatternDrag || (() => {}),
      onChange: opts.onChange || (() => {}),
      getEngine: opts.getEngine || (() => null),
      loadBuffer: opts.loadBuffer || (async () => null),
      resolveDropItem: opts.resolveDropItem || null,
      previewPlaying: false,
      previewSchedulerId: null,
      previewCurrentStep: 0,
      previewNextStepTime: 0,
      previewGain: null,
      previewStartTime: 0,
      previewPlayheadRAF: null,
   };

   // ---- Piano roll view state (scroll/zoom) ----
   const view = {
      scrollY: 60 * 14, // start scrolled to show C4 area
      scrollX: 0,
      cellW: 24,
      cellH: 14,
      keyW: 56,
      headerH: 20,
      velLaneH: 50, // velocity lane height at bottom
   };

   // ---- Drag state ----
   const drag = {
      active: false,
      mode: null,   // 'move', 'resizeL', 'resizeR', 'velocity'
      note: null,
      startX: 0,
      startY: 0,
      origStart: 0,
      origLen: 0,
      origMidi: 0,
   };

   // ---- Velocity lane drag state ----
   const velDrag = { active: false, note: null };

   // ---- Selected note (for velocity editing) ----
   let selectedNote = null;

   // ---- ADSR drag state ----
   const adsrDrag = {
      active: false,
      param: null,  // 'attack', 'decay', 'release'
      lockedLayout: null,
   };

   container.innerHTML = '';

   const wrapper = document.createElement('div');
   wrapper.className = 'lw-pianoroll-wrapper';
   wrapper.style.cssText = 'display:flex;flex-direction:row;width:100%;height:100%;overflow:hidden;';

   // ---- Left column: pattern list ----
   const leftCol = document.createElement('div');
   leftCol.style.cssText = 'width:180px;min-width:120px;border-right:1px solid var(--c2);display:flex;flex-direction:column;overflow:hidden;';

   const listHeader = document.createElement('div');
   listHeader.style.cssText = 'padding:4px 8px;border-bottom:1px solid var(--c2);display:flex;align-items:center;gap:4px;';
   listHeader.innerHTML = '<span style="font-family:bit,monospace;font-size:10px;color:var(--ce);flex:1;">Patterns</span>';
   const addBtn = document.createElement('button');
   addBtn.textContent = '+';
   addBtn.className = 'lw-btn-sm';
   addBtn.title = 'New pattern';
   addBtn.onclick = () => createNewPattern();
   listHeader.appendChild(addBtn);
   leftCol.appendChild(listHeader);

   const listEl = document.createElement('div');
   listEl.style.cssText = 'flex:1;overflow-y:auto;';
   leftCol.appendChild(listEl);

   // ---- Right column: editor ----
   const rightCol = document.createElement('div');
   rightCol.style.cssText = 'flex:1;display:flex;flex-direction:column;overflow:hidden;';

   const editorHeader = document.createElement('div');
   editorHeader.style.cssText = 'padding:4px 8px;border-bottom:1px solid var(--c2);display:flex;align-items:center;gap:6px;flex-wrap:wrap;';
   rightCol.appendChild(editorHeader);

   const editorBody = document.createElement('div');
   editorBody.style.cssText = 'flex:1;overflow:hidden;position:relative;';
   rightCol.appendChild(editorBody);

   wrapper.appendChild(leftCol);
   wrapper.appendChild(rightCol);
   container.appendChild(wrapper);

   // ---- Theme colors ----
   function colors() {
      const cs = getComputedStyle(document.documentElement);
      return {
         bg: (cs.getPropertyValue('--c0') || '#111').trim(),
         bg2: (cs.getPropertyValue('--c1') || '#222').trim(),
         grid: (cs.getPropertyValue('--c2') || '#333').trim(),
         gridMajor: (cs.getPropertyValue('--c4') || '#555').trim(),
         note: (cs.getPropertyValue('--cb') || '#6b8').trim(),
         noteSel: (cs.getPropertyValue('--c9') || '#4a9').trim(),
         noteBorder: (cs.getPropertyValue('--c4') || '#555').trim(),
         text: (cs.getPropertyValue('--cf') || '#fff').trim(),
         textDim: (cs.getPropertyValue('--c6') || '#666').trim(),
         textMid: (cs.getPropertyValue('--ce') || '#aaa').trim(),
         accent: (cs.getPropertyValue('--c9') || '#4a9').trim(),
      };
   }

   // ---- Create new pattern ----
   function createNewPattern() {
      const pat = {
         uid: 'pat_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7),
         name: 'Pattern ' + (state.patterns.length + 1),
         sampleId: null,
         sampleName: '(no sample)',
         audioBuffer: null,
         beats: DEFAULT_BEATS,
         bpm: parseFloat(document.getElementById('lw-bpm') && document.getElementById('lw-bpm').value) || 120,
         notes: [],
         loopStart: 0,
         loopEnd: 0,
         adsr: { attack: 0.01, decay: 0.15, sustain: 0.7, release: 0.2 },
      };
      state.patterns.push(pat);
      state.activePatternIdx = state.patterns.length - 1;
      renderList();
      renderEditor();
   }

   // ---- Render pattern list ----
   function renderList() {
      listEl.innerHTML = '';
      for (let i = 0; i < state.patterns.length; i++) {
         const pat = state.patterns[i];
         const item = document.createElement('div');
         item.style.cssText = 'padding:4px 8px;cursor:pointer;font-family:bit,monospace;font-size:10px;display:flex;align-items:center;gap:4px;border-bottom:1px solid var(--c2);';
         if (i === state.activePatternIdx) {
            item.style.background = 'var(--c3)';
            item.style.color = 'var(--cf)';
         } else {
            item.style.color = 'var(--ce)';
         }
         item.textContent = pat.name;
         item.draggable = true;
         item.onclick = () => {
            if (state.previewPlaying) stopPreview();
            state.activePatternIdx = i;
            renderList();
            renderEditor();
         };
         item.ondragstart = (e) => {
            e.dataTransfer.setData('text/dawseur-pianoroll', JSON.stringify({
               type: 'pianoroll',
               patternIdx: i,
            }));
            e.dataTransfer.effectAllowed = 'copy';
         };
         item.ondblclick = () => {
            const name = prompt('Pattern name:', pat.name);
            if (name) { pat.name = name; renderList(); }
         };

         // Clone button
         const cloneBtn = document.createElement('span');
         cloneBtn.textContent = '\u23c8';
         cloneBtn.title = 'Clone pattern';
         cloneBtn.style.cssText = 'margin-left:auto;color:var(--c6);cursor:pointer;padding:0 2px;';
         cloneBtn.onclick = (e) => {
            e.stopPropagation();
            const clone = {
               name: pat.name + ' copy',
               sampleId: pat.sampleId,
               sampleName: pat.sampleName,
               audioBuffer: pat.audioBuffer,
               beats: pat.beats,
               bpm: pat.bpm,
               notes: pat.notes.map(n => ({ ...n })),
               loopStart: pat.loopStart,
               loopEnd: pat.loopEnd,
               adsr: { ...pat.adsr },
            };
            state.patterns.splice(i + 1, 0, clone);
            state.activePatternIdx = i + 1;
            renderList();
            renderEditor();
            state.onChange();
         };
         item.appendChild(cloneBtn);

         // Delete button
         const delBtn = document.createElement('span');
         delBtn.textContent = 'x';
         delBtn.title = 'Delete pattern';
         delBtn.style.cssText = 'color:var(--c6);cursor:pointer;padding:0 2px;';
         delBtn.onclick = (e) => {
            e.stopPropagation();
            state.patterns.splice(i, 1);
            if (state.activePatternIdx >= state.patterns.length) state.activePatternIdx = state.patterns.length - 1;
            if (state.previewPlaying) stopPreview();
            renderList();
            renderEditor();
         };
         item.appendChild(delBtn);
         listEl.appendChild(item);
      }
      if (state.patterns.length === 0) {
         listEl.innerHTML = '<div style="padding:8px;font-family:bit,monospace;font-size:10px;color:var(--c6);">No patterns. Click + to create one.</div>';
      }
   }

   // ---- Render editor (tabbed) ----
   function renderEditor() {
      const pat = state.patterns[state.activePatternIdx];
      editorHeader.innerHTML = '';
      editorBody.innerHTML = '';
      if (!pat) {
         editorBody.innerHTML = '<div style="padding:8px;font-family:bit,monospace;font-size:10px;color:var(--c6);">Select or create a pattern.</div>';
         return;
      }

      // ---- Tab bar ----
      const tabBar = document.createElement('div');
      tabBar.style.cssText = 'display:flex;gap:0;';
      const tabs = [
         { id: 'sample', label: 'Sample' },
         { id: 'pianoroll', label: 'Piano Roll' },
         { id: 'adsr', label: 'ADSR' },
      ];
      for (const tab of tabs) {
         const btn = document.createElement('button');
         btn.textContent = tab.label;
         const isActive = state.activeTab === tab.id;
         btn.style.cssText = `border:none;border-bottom:2px solid ${isActive ? 'var(--cb)' : 'transparent'};background:${isActive ? 'var(--c3)' : 'transparent'};color:${isActive ? 'var(--cf)' : 'var(--c6)'};font-family:bit,monospace;font-size:10px;padding:4px 10px;cursor:pointer;white-space:nowrap;`;
         btn.onclick = () => { state.activeTab = tab.id; renderEditor(); };
         tabBar.appendChild(btn);
      }
      editorHeader.appendChild(tabBar);

      if (state.activeTab === 'pianoroll') renderPianoRollTab(pat);
      else if (state.activeTab === 'sample') renderSampleTab(pat);
      else if (state.activeTab === 'adsr') renderADSRTab(pat);
   }

   // =====================================================================
   // PIANO ROLL TAB — custom canvas-based editor
   // =====================================================================
   function renderPianoRollTab(pat) {
      const body = document.createElement('div');
      body.style.cssText = 'display:flex;flex-direction:column;width:100%;height:100%;overflow:hidden;';

      // ---- Controls row ----
      const ctrlRow = document.createElement('div');
      ctrlRow.style.cssText = 'display:flex;align-items:center;gap:6px;padding:4px 8px;border-bottom:1px solid var(--c2);flex-wrap:wrap;flex-shrink:0;';

      const sampleLabel = document.createElement('span');
      sampleLabel.style.cssText = 'font-family:bit,monospace;font-size:10px;color:var(--ce);';
      sampleLabel.textContent = pat.sampleName ? ('Sample: ' + pat.sampleName) : 'Sample: (none - use Sample tab)';
      ctrlRow.appendChild(sampleLabel);

      const beatsLabel = document.createElement('span');
      beatsLabel.style.cssText = 'font-family:bit,monospace;font-size:10px;color:var(--ce);margin-left:4px;';
      beatsLabel.textContent = 'Beats:';
      ctrlRow.appendChild(beatsLabel);

      const beatsSelect = document.createElement('select');
      beatsSelect.style.cssText = 'background:var(--c0);border:1px solid var(--c4);color:var(--cf);font-family:bit,monospace;font-size:10px;';
      [1, 2, 4, 8, 16].forEach(n => {
         const opt = document.createElement('option');
         opt.value = n; opt.textContent = n;
         if (n === pat.beats) opt.selected = true;
         beatsSelect.appendChild(opt);
      });
      beatsSelect.onchange = () => {
         pat.beats = parseInt(beatsSelect.value);
         renderEditor();
         state.onChange();
      };
      ctrlRow.appendChild(beatsSelect);

      const playBtn = document.createElement('button');
      playBtn.id = 'lw-pianoroll-preview-btn';
      playBtn.textContent = state.previewPlaying ? '\uE801' : '\uE800';
      playBtn.style.cssText = 'margin-left:4px;color:' + (state.previewPlaying ? 'var(--c6)' : 'var(--c9)') + ';border:1px solid var(--c4);background:var(--c0);font-family:icons;font-size:12px;cursor:pointer;padding:2px 8px;';
      playBtn.onclick = (e) => { e.stopPropagation(); togglePreview(); };
      ctrlRow.appendChild(playBtn);

      const clearBtn = document.createElement('button');
      clearBtn.textContent = 'Clear';
      clearBtn.className = 'lw-btn-text';
      clearBtn.style.cssText = 'margin-left:4px;';
      clearBtn.onclick = () => { pat.notes = []; state.onChange(); renderEditor(); };
      ctrlRow.appendChild(clearBtn);

      const velHint = document.createElement('span');
      velHint.style.cssText = 'font-family:bit,monospace;font-size:9px;color:var(--c6);margin-left:8px;';
      velHint.textContent = 'Click note to select | Drag velocity bars at bottom to set selected note velocity | Shift/Right-click: delete';
      ctrlRow.appendChild(velHint);

      body.appendChild(ctrlRow);

      // ---- Canvas container (canvas + vertical scrollbar) ----
      const canvasContainer = document.createElement('div');
      canvasContainer.style.cssText = 'flex:1;position:relative;overflow:hidden;display:flex;';
      body.appendChild(canvasContainer);

      const canvasWrap = document.createElement('div');
      canvasWrap.style.cssText = 'flex:1;position:relative;overflow:hidden;';
      canvasContainer.appendChild(canvasWrap);

      const canvas = document.createElement('canvas');
      canvas.style.cssText = 'display:block;cursor:crosshair;';
      canvasWrap.appendChild(canvas);

      // Vertical scrollbar
      const scrollBar = document.createElement('div');
      scrollBar.style.cssText = 'width:14px;height:100%;background:var(--c1);border-left:1px solid var(--c2);position:relative;flex-shrink:0;';
      canvasContainer.appendChild(scrollBar);

      const scrollHandle = document.createElement('div');
      scrollHandle.style.cssText = 'position:absolute;top:0;width:100%;background:var(--c4);cursor:grab;border-radius:1px;';
      scrollBar.appendChild(scrollHandle);

      let cw = 0, ch = 0;
      function resizeCanvas() {
         cw = canvasWrap.clientWidth;
         ch = canvasWrap.clientHeight;
         if (cw < 10 || ch < 10) return;
         canvas.width = cw;
         canvas.height = ch;
         drawGrid();
         updateScrollBar();
      }

      const ro = new ResizeObserver(() => resizeCanvas());
      ro.observe(canvasWrap);
      requestAnimationFrame(resizeCanvas);

      // ---- Scrollbar logic ----
      function getMaxScrollY() {
         return Math.max(0, (MIDI_MAX - MIDI_MIN + 1) * view.cellH - ch + view.headerH);
      }

      function updateScrollBar() {
         const maxScroll = getMaxScrollY();
         if (maxScroll <= 0) {
            scrollHandle.style.height = '100%';
            scrollHandle.style.top = '0px';
            return;
         }
         const ratio = view.scrollY / maxScroll;
         const handleH = Math.max(20, (ch / ((MIDI_MAX - MIDI_MIN + 1) * view.cellH)) * ch);
         scrollHandle.style.height = handleH + 'px';
         scrollHandle.style.top = (ratio * (ch - handleH)) + 'px';
      }

      // Drag scrollbar handle
      let sbDrag = { active: false, startY: 0, startScroll: 0 };
      scrollHandle.onmousedown = (e) => {
         sbDrag.active = true;
         sbDrag.startY = e.clientY;
         sbDrag.startScroll = view.scrollY;
         scrollHandle.style.cursor = 'grabbing';
         e.preventDefault();
         e.stopPropagation();
      };
      const sbMoveHandler = (e) => {
         if (!sbDrag.active) return;
         const maxScroll = getMaxScrollY();
         if (maxScroll <= 0) return;
         const dy = e.clientY - sbDrag.startY;
         const handleH = parseFloat(scrollHandle.style.height) || 20;
         const scrollRange = ch - handleH;
         const scrollDelta = (dy / scrollRange) * maxScroll;
         view.scrollY = Math.max(0, Math.min(maxScroll, sbDrag.startScroll + scrollDelta));
         drawGrid();
         updateScrollBar();
      };
      const sbUpHandler = () => {
         if (sbDrag.active) {
            sbDrag.active = false;
            scrollHandle.style.cursor = 'grab';
         }
      };
      document.addEventListener('mousemove', sbMoveHandler);
      document.addEventListener('mouseup', sbUpHandler);

      // Click on scrollbar track to jump
      scrollBar.onmousedown = (e) => {
         if (e.target === scrollHandle) return; // handle drag handles it
         const rect = scrollBar.getBoundingClientRect();
         const clickY = e.clientY - rect.top;
         const handleH = parseFloat(scrollHandle.style.height) || 20;
         const maxScroll = getMaxScrollY();
         const targetRatio = (clickY - handleH / 2) / (ch - handleH);
         view.scrollY = Math.max(0, Math.min(maxScroll, targetRatio * maxScroll));
         drawGrid();
         updateScrollBar();
      };

      // ---- Drawing ----
      const totalSteps = pat.beats * STEPS_PER_BEAT;

      function drawGrid() {
         const ctx = canvas.getContext('2d');
         const col = colors();
         const kw = view.keyW;
         const hh = view.headerH;
         const cellW = view.cellW;
         const cellH = view.cellH;

         // Visible note range
         const firstMidi = MIDI_MAX - Math.floor((view.scrollY + ch - hh) / cellH);
         const lastMidi = MIDI_MAX - Math.ceil((view.scrollY) / cellH);
         const visMin = Math.max(MIDI_MIN, firstMidi);
         const visMax = Math.min(MIDI_MAX, lastMidi);

         // Clear
         ctx.fillStyle = col.bg;
         ctx.fillRect(0, 0, cw, ch);

         // ---- Keyboard column background ----
         ctx.fillStyle = col.bg2;
         ctx.fillRect(0, 0, kw, ch);

         // ---- Row backgrounds (black/white key stripes) ----
         for (let m = visMin; m <= visMax; m++) {
            const y = hh + (MIDI_MAX - m) * cellH - view.scrollY;
            const isBlack = BLACK_KEYS.includes(m % 12);
            ctx.fillStyle = isBlack ? col.bg2 : col.bg;
            ctx.fillRect(kw, y, cw - kw, cellH);
         }

         // ---- Horizontal grid lines ----
         for (let m = visMin; m <= visMax + 1; m++) {
            const y = hh + (MIDI_MAX - m) * cellH - view.scrollY;
            const isC = m % 12 === 0;
            ctx.strokeStyle = isC ? col.gridMajor : col.grid;
            ctx.lineWidth = isC ? 1 : 0.5;
            ctx.beginPath();
            ctx.moveTo(kw, y);
            ctx.lineTo(cw, y);
            ctx.stroke();
         }

         // ---- Vertical grid lines ----
         const firstStep = Math.floor(view.scrollX / cellW);
         const lastStep = Math.ceil((view.scrollX + cw - kw) / cellW);
         for (let s = firstStep; s <= lastStep; s++) {
            const x = kw + s * cellW - view.scrollX;
            const isBeat = s % STEPS_PER_BEAT === 0;
            ctx.strokeStyle = isBeat ? col.gridMajor : col.grid;
            ctx.lineWidth = isBeat ? 1 : 0.5;
            ctx.beginPath();
            ctx.moveTo(x, hh);
            ctx.lineTo(x, ch);
            ctx.stroke();
         }

         // ---- Header (time ruler) ----
         ctx.fillStyle = col.bg2;
         ctx.fillRect(0, 0, cw, hh);
         ctx.strokeStyle = col.gridMajor;
         ctx.lineWidth = 1;
         ctx.beginPath();
         ctx.moveTo(0, hh);
         ctx.lineTo(cw, hh);
         ctx.stroke();

         // Beat numbers
         ctx.fillStyle = col.textDim;
         ctx.font = '9px monospace';
         ctx.textAlign = 'left';
         for (let s = firstStep; s <= lastStep; s++) {
            if (s % STEPS_PER_BEAT === 0 && s >= 0 && s <= totalSteps) {
               const x = kw + s * cellW - view.scrollX;
               ctx.fillText(String(s / STEPS_PER_BEAT + 1), x + 3, hh - 5);
            }
         }

         // ---- Keyboard (left column) ----
         for (let m = visMin; m <= visMax; m++) {
            const y = hh + (MIDI_MAX - m) * cellH - view.scrollY;
            const noteIdx = m % 12;
            const isBlack = BLACK_KEYS.includes(noteIdx);
            // White key
            if (!isBlack) {
               ctx.fillStyle = col.text;
               ctx.fillRect(0, y, kw, cellH);
            }
            // Black key overlay
            if (isBlack) {
               ctx.fillStyle = col.bg;
               ctx.fillRect(0, y, kw * 0.62, cellH);
            }
            // Key border
            ctx.strokeStyle = col.gridMajor;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.lineTo(kw, y);
            ctx.stroke();
            // C note label
            if (noteIdx === 0) {
               ctx.fillStyle = col.textDim;
               ctx.font = '8px monospace';
               ctx.textAlign = 'right';
               ctx.fillText(midiToNoteName(m), kw - 3, y + cellH - 3);
            }
         }
         // Keyboard right border
         ctx.strokeStyle = col.gridMajor;
         ctx.lineWidth = 1;
         ctx.beginPath();
         ctx.moveTo(kw, 0);
         ctx.lineTo(kw, ch);
         ctx.stroke();

         // ---- Notes ----
         for (const note of pat.notes) {
            const nx = kw + note.startStep * cellW - view.scrollX;
            const ny = hh + (MIDI_MAX - note.midi) * cellH - view.scrollY;
            const nw = note.lengthSteps * cellW;
            const nh = cellH;
            const vel = note.velocity !== undefined ? note.velocity : 1.0;

            // Skip if off-screen (don't draw into velocity lane)
            const velTop = ch - view.velLaneH;
            if (nx + nw < kw || nx > cw || ny + nh < hh || ny > velTop) continue;

            // Note body — selected note uses accent color
            const isSelected = note === selectedNote;
            ctx.globalAlpha = isSelected ? 1 : (0.3 + vel * 0.7);
            ctx.fillStyle = isSelected ? col.noteSel : col.note;
            ctx.fillRect(nx, ny + 1, nw - 1, nh - 2);
            // Border
            ctx.strokeStyle = isSelected ? col.accent : col.noteBorder;
            ctx.lineWidth = isSelected ? 2 : 1;
            ctx.strokeRect(nx, ny + 1, nw - 1, nh - 2);
            ctx.globalAlpha = 1;
            // Resize handles (left and right edges highlighted)
            ctx.fillStyle = col.noteSel;
            ctx.fillRect(nx, ny + 1, 2, nh - 2);
            ctx.fillRect(nx + nw - 3, ny + 1, 2, nh - 2);
         }

         // ---- Velocity lane (bottom of canvas) ----
         const velTop = ch - view.velLaneH;
         const velBottom = ch;
         // Background
         ctx.fillStyle = col.bg2;
         ctx.fillRect(kw, velTop, cw - kw, view.velLaneH);
         // Divider line
         ctx.strokeStyle = col.gridMajor;
         ctx.lineWidth = 1;
         ctx.beginPath();
         ctx.moveTo(kw, velTop);
         ctx.lineTo(cw, velTop);
         ctx.stroke();
         // Horizontal grid lines in velocity lane
         ctx.strokeStyle = col.grid;
         ctx.lineWidth = 0.5;
         for (let i = 1; i < 4; i++) {
            const y = velTop + (i / 4) * view.velLaneH;
            ctx.beginPath();
            ctx.moveTo(kw, y);
            ctx.lineTo(cw, y);
            ctx.stroke();
         }
         // "VEL" label (placed after the keyboard column so it doesn't overlap keys)
         ctx.fillStyle = col.textDim;
         ctx.font = '8px monospace';
         ctx.textAlign = 'left';
         ctx.fillText('VEL', kw + 4, velTop + 10);
         // Velocity bars for each note
         for (const note of pat.notes) {
            const nx = kw + note.startStep * cellW - view.scrollX;
            const nw = note.lengthSteps * cellW;
            if (nx + nw < kw || nx > cw) continue; // skip off-screen
            const vel = note.velocity !== undefined ? note.velocity : 1.0;
            const barH = vel * (view.velLaneH - 4);
            const barX = nx + 1;
            const barW = Math.max(3, nw - 2);
            const barY = velBottom - barH - 2;
            // Bar — selected note highlighted
            const isSelected = note === selectedNote;
            const isDragging = velDrag.note === note;
            ctx.fillStyle = isDragging ? col.accent : (isSelected ? col.noteSel : col.note);
            ctx.globalAlpha = isSelected || isDragging ? 1 : 0.5;
            ctx.fillRect(barX, barY, barW, barH);
            ctx.globalAlpha = 1;
            // Top cap
            ctx.fillStyle = col.accent;
            ctx.fillRect(barX, barY, barW, 2);
         }

         // ---- Playhead ----
         if (state.previewPlaying) {
            const secPerBeat = 60 / (pat.bpm || parseFloat(document.getElementById('lw-bpm') && document.getElementById('lw-bpm').value) || 120);
            const secPerStep = secPerBeat / STEPS_PER_BEAT;
            const elapsed = state.previewGain && state.previewGain.context.currentTime - state.previewStartTime;
            if (elapsed !== undefined && elapsed >= 0) {
               const stepF = (elapsed / secPerStep) % totalSteps;
               const px = kw + stepF * cellW - view.scrollX;
               ctx.strokeStyle = col.accent;
               ctx.lineWidth = 2;
               ctx.beginPath();
               ctx.moveTo(px, hh);
               ctx.lineTo(px, ch);
               ctx.stroke();
            }
         }
      }

      // ---- Coordinate helpers ----
      function pxToStep(px) {
         return (px - view.keyW + view.scrollX) / view.cellW;
      }
      function pxToMidi(py) {
         return MIDI_MAX - Math.floor((py - view.headerH + view.scrollY) / view.cellH);
      }
      function midiToY(midi) {
         return view.headerH + (MIDI_MAX - midi) * view.cellH - view.scrollY;
      }
      function stepToX(step) {
         return view.keyW + step * view.cellW - view.scrollX;
      }

      function findNoteAt(step, midi) {
         return pat.notes.find(n =>
            n.midi === midi && step >= n.startStep && step < n.startStep + n.lengthSteps
         );
      }

      // ---- Mouse interaction ----
      const RESIZE_EDGE = 5; // pixels from edge to trigger resize

      canvas.onmousedown = (e) => {
         const rect = canvas.getBoundingClientRect();
         const mx = e.clientX - rect.left;
         const my = e.clientY - rect.top;

         // Click on keyboard: preview note
         if (mx < view.keyW) {
            if (my < view.headerH) return;
            const midi = pxToMidi(my);
            if (midi >= MIDI_MIN && midi <= MIDI_MAX) {
               previewNote(pat, midi);
            }
            return;
         }
         if (my < view.headerH) return;

         // ---- Velocity lane: drag selected note's velocity ----
         const velTop = ch - view.velLaneH;
         if (my >= velTop) {
            // If no note selected, try to find one at this step
            if (!selectedNote) {
               const stepF = pxToStep(mx);
               const step = Math.floor(stepF);
               selectedNote = pat.notes.find(n => step >= n.startStep && step < n.startStep + n.lengthSteps) || null;
            }
            if (selectedNote) {
               velDrag.active = true;
               velDrag.note = selectedNote;
               // Set velocity from click position
               const vel = 1 - (my - velTop) / view.velLaneH;
               selectedNote.velocity = Math.max(0, Math.min(1, vel));
               drawGrid();
               state.onChange();
            }
            e.preventDefault();
            return;
         }

         const stepF = pxToStep(mx);
         const step = Math.floor(stepF);
         const midi = pxToMidi(my);
         if (midi < MIDI_MIN || midi > MIDI_MAX) return;

         const existing = findNoteAt(step, midi);
         if (existing) {
            if (e.button === 2 || e.shiftKey) {
               // Delete
               pat.notes = pat.notes.filter(n => n !== existing);
               if (selectedNote === existing) selectedNote = null;
               state.onChange();
               drawGrid();
            } else {
               // Select this note
               selectedNote = existing;
               // Check edges for resize
               const noteX = stepToX(existing.startStep);
               const noteRightX = stepToX(existing.startStep + existing.lengthSteps);
               if (Math.abs(mx - noteX) < RESIZE_EDGE) {
                  drag.mode = 'resizeL';
               } else if (Math.abs(mx - noteRightX) < RESIZE_EDGE) {
                  drag.mode = 'resizeR';
               } else {
                  drag.mode = 'move';
               }
               drag.active = true;
               drag.note = existing;
               drag.startX = mx;
               drag.startY = my;
               drag.origStart = existing.startStep;
               drag.origLen = existing.lengthSteps;
               drag.origMidi = existing.midi;
               drawGrid();
            }
         } else {
            // Create new note
            if (step < 0 || step >= totalSteps) return;
            const newNote = { midi, startStep: step, lengthSteps: 1, velocity: 1.0 };
            pat.notes.push(newNote);
            selectedNote = newNote;
            drag.active = true;
            drag.mode = 'resizeR';
            drag.note = newNote;
            drag.startX = mx;
            drag.startY = my;
            drag.origStart = step;
            drag.origLen = 1;
            drag.origMidi = midi;
            state.onChange();
            drawGrid();
         }
         e.preventDefault();
      };

      canvas.onmousemove = (e) => {
         const rect = canvas.getBoundingClientRect();
         const mx = e.clientX - rect.left;
         const my = e.clientY - rect.top;

         // ---- Velocity lane drag ----
         if (velDrag.active && velDrag.note) {
            const velTop = ch - view.velLaneH;
            const vel = 1 - (my - velTop) / view.velLaneH;
            velDrag.note.velocity = Math.max(0, Math.min(1, vel));
            drawGrid();
            state.onChange();
            return;
         }

         if (!drag.active) {
            // Update cursor based on hover position
            if (mx < view.keyW) {
               canvas.style.cursor = 'pointer';
               return;
            }
            if (my < view.headerH) {
               canvas.style.cursor = 'default';
               return;
            }
            // Velocity lane hover
            const velTop = ch - view.velLaneH;
            if (my >= velTop) {
               // Show resize cursor if there's a selected note, or a note at this step
               const stepF = pxToStep(mx);
               const step = Math.floor(stepF);
               const noteAtStep = pat.notes.find(n => step >= n.startStep && step < n.startStep + n.lengthSteps);
               canvas.style.cursor = (selectedNote || noteAtStep) ? 'ns-resize' : 'default';
               return;
            }
            const stepF = pxToStep(mx);
            const step = Math.floor(stepF);
            const midi = pxToMidi(my);
            const hover = findNoteAt(step, midi);
            if (hover) {
               const noteX = stepToX(hover.startStep);
               const noteRightX = stepToX(hover.startStep + hover.lengthSteps);
               const nearLeft = Math.abs(mx - noteX) < RESIZE_EDGE;
               const nearRight = Math.abs(mx - noteRightX) < RESIZE_EDGE;
               if (nearLeft && nearRight) {
                  // Very short note - both edges overlap
                  canvas.style.cursor = 'ew-resize';
               } else if (nearLeft) {
                  canvas.style.cursor = 'w-resize';
               } else if (nearRight) {
                  canvas.style.cursor = 'e-resize';
               } else {
                  canvas.style.cursor = 'move';
               }
            } else {
               canvas.style.cursor = 'crosshair';
            }
            return;
         }

         if (!drag.note) return;
         // Update cursor during drag
         if (drag.mode === 'resizeL') canvas.style.cursor = 'w-resize';
         else if (drag.mode === 'resizeR') canvas.style.cursor = 'e-resize';
         else canvas.style.cursor = 'move';

         const dxSteps = (mx - drag.startX) / view.cellW;
         const dyMidi = -Math.round((my - drag.startY) / view.cellH);

         if (drag.mode === 'move') {
            drag.note.startStep = Math.max(0, Math.min(totalSteps - 1, drag.origStart + dxSteps));
            drag.note.midi = Math.max(MIDI_MIN, Math.min(MIDI_MAX, drag.origMidi + dyMidi));
         } else if (drag.mode === 'resizeR') {
            drag.note.lengthSteps = Math.max(1, drag.origLen + dxSteps);
         } else if (drag.mode === 'resizeL') {
            const newStart = drag.origStart + dxSteps;
            const newLen = drag.origLen - dxSteps;
            if (newLen >= 1) {
               drag.note.startStep = Math.max(0, newStart);
               drag.note.lengthSteps = newLen;
            }
         }
         drawGrid();
      };

      function endDrag() {
         if (drag.active && drag.note) {
            // Snap to integer
            drag.note.startStep = Math.round(drag.note.startStep);
            drag.note.startStep = Math.max(0, Math.min(totalSteps - 1, drag.note.startStep));
            drag.note.lengthSteps = Math.round(drag.note.lengthSteps);
            drag.note.lengthSteps = Math.max(1, drag.note.lengthSteps);
            if (drag.note.startStep + drag.note.lengthSteps > totalSteps) {
               drag.note.lengthSteps = totalSteps - drag.note.startStep;
            }
            state.onChange();
            drawGrid();
         }
         drag.active = false;
         drag.mode = null;
         drag.note = null;
         velDrag.active = false;
         velDrag.note = null;
      }

      canvas.onmouseup = endDrag;
      document.addEventListener('mouseup', endDrag);
      canvas.oncontextmenu = (e) => e.preventDefault();

      // ---- Mouse wheel: scroll / zoom ----
      canvas.onwheel = (e) => {
         e.preventDefault();
         if (e.ctrlKey) {
            // Zoom
            const delta = e.deltaY > 0 ? -2 : 2;
            view.cellW = Math.max(8, Math.min(60, view.cellW + delta));
         } else if (e.shiftKey) {
            // Horizontal scroll
            view.scrollX = Math.max(0, view.scrollX + e.deltaY);
         } else {
            // Vertical scroll
            view.scrollY = Math.max(0, view.scrollY + e.deltaY);
            const maxScroll = getMaxScrollY();
            if (view.scrollY > maxScroll) view.scrollY = maxScroll;
         }
         drawGrid();
         updateScrollBar();
      };

      // ---- Playhead animation ----
      function animatePlayhead() {
         if (!state.previewPlaying) return;
         drawGrid();
         state.previewPlayheadRAF = requestAnimationFrame(animatePlayhead);
      }

      // Store drawGrid for external use
      pat._drawGrid = drawGrid;
      pat._animatePlayhead = animatePlayhead;

      editorBody.appendChild(body);
   }

   // ---- Preview a single note (keyboard click) ----
   function previewNote(pat, midi) {
      const eng = state.getEngine();
      if (!eng || !pat.sampleId) return;
      let buffer = pat.audioBuffer;
      if (!buffer && eng.getMIDISampleBuffer) buffer = eng.getMIDISampleBuffer(pat.sampleId);
      if (!buffer) return;
      const ctx = eng.ctx;
      if (ctx.state === 'suspended') ctx.resume();
      const playbackRate = semitoneToPlaybackRate(midi);
      const tmpGain = ctx.createGain();
      tmpGain.gain.value = 0.7;
      tmpGain.connect(ctx.destination);
      const noteDurSec = 0.5;
      const loopOn = pat.loopEnabled && pat.loopEnd > pat.loopStart;
      if (loopOn && eng.triggerSampleLooped) {
         eng.triggerSampleLooped(buffer, tmpGain, ctx.currentTime + 0.01, 0.7, playbackRate, 0, noteDurSec, pat.adsr, true, pat.loopStart, pat.loopEnd);
      } else if (eng.triggerSampleWithADSR && pat.adsr) {
         eng.triggerSampleWithADSR(buffer, tmpGain, ctx.currentTime + 0.01, 0.7, playbackRate, 0, 0, noteDurSec, pat.adsr);
      } else {
         eng.triggerSample(buffer, tmpGain, ctx.currentTime + 0.01, 0.7, playbackRate, 0, 0, noteDurSec);
      }
      const releaseSec = pat.adsr ? pat.adsr.release : 0;
      setTimeout(() => { try { tmpGain.disconnect(); } catch (e) {} }, (noteDurSec + releaseSec + 0.3) * 1000);
   }

   // =====================================================================
   // SAMPLE TAB
   // =====================================================================
   function renderSampleTab(pat) {
      const body = document.createElement('div');
      body.style.cssText = 'padding:8px;display:flex;flex-direction:column;gap:8px;overflow:auto;height:100%;';

      // Sample name + clear button
      const sampleRow = document.createElement('div');
      sampleRow.style.cssText = 'display:flex;align-items:center;gap:6px;flex-wrap:wrap;';
      const sampleLabel = document.createElement('span');
      sampleLabel.style.cssText = 'font-family:bit,monospace;font-size:10px;color:var(--ce);';
      sampleLabel.textContent = pat.sampleName ? ('Sample: ' + pat.sampleName) : 'Sample: (drag & drop below)';
      sampleRow.appendChild(sampleLabel);
      if (pat.sampleId) {
         const clearSampleBtn = document.createElement('button');
         clearSampleBtn.textContent = 'x';
         clearSampleBtn.title = 'Remove sample';
         clearSampleBtn.className = 'lw-btn-text';
         clearSampleBtn.style.cssText = 'padding:1px 6px;font-size:9px;';
         clearSampleBtn.onclick = () => {
            pat.sampleId = null;
            pat.sampleName = '(no sample)';
            pat.audioBuffer = null;
            pat.loopStart = 0;
            pat.loopEnd = 0;
            pat.loopEnabled = false;
            state.onChange();
            renderEditor();
         };
         sampleRow.appendChild(clearSampleBtn);
      }
      body.appendChild(sampleRow);

      // Loop checkbox + zoom controls
      const ctrlRow = document.createElement('div');
      ctrlRow.style.cssText = 'display:flex;align-items:center;gap:8px;flex-wrap:wrap;';

      // Loop checkbox
      const loopCheckWrap = document.createElement('label');
      loopCheckWrap.style.cssText = 'display:flex;align-items:center;gap:4px;cursor:pointer;font-family:bit,monospace;font-size:10px;color:var(--ce);';
      const loopCheck = document.createElement('input');
      loopCheck.type = 'checkbox';
      loopCheck.checked = !!pat.loopEnabled;
      loopCheck.style.cssText = 'cursor:pointer;accent-color:var(--c9);';
      loopCheck.onchange = () => {
         pat.loopEnabled = loopCheck.checked;
         if (pat.loopEnabled && pat.audioBuffer) {
            // Initialize loop to full sample if not set
            if (pat.loopEnd <= pat.loopStart) {
               pat.loopStart = 0;
               pat.loopEnd = pat.audioBuffer.length;
            }
         }
         state.onChange();
         renderEditor();
      };
      loopCheckWrap.appendChild(loopCheck);
      loopCheckWrap.appendChild(document.createTextNode('Loop'));
      ctrlRow.appendChild(loopCheckWrap);

      // Zoom buttons
      const zoomInBtn = document.createElement('button');
      zoomInBtn.textContent = '+';
      zoomInBtn.className = 'lw-btn-text';
      zoomInBtn.style.cssText = 'padding:1px 6px;font-size:11px;min-width:20px;';
      zoomInBtn.title = 'Zoom In';
      zoomInBtn.onclick = () => { sampleZoom *= 1.5; if (sampleZoom > 50) sampleZoom = 50; drawWave(); updateScrollbar(); };
      ctrlRow.appendChild(zoomInBtn);

      const zoomOutBtn = document.createElement('button');
      zoomOutBtn.textContent = '-';
      zoomOutBtn.className = 'lw-btn-text';
      zoomOutBtn.style.cssText = 'padding:1px 6px;font-size:11px;min-width:20px;';
      zoomOutBtn.title = 'Zoom Out';
      zoomOutBtn.onclick = () => { sampleZoom /= 1.5; if (sampleZoom < 1) sampleZoom = 1; drawWave(); updateScrollbar(); };
      ctrlRow.appendChild(zoomOutBtn);

      const zoomResetBtn = document.createElement('button');
      zoomResetBtn.textContent = '1:1';
      zoomResetBtn.className = 'lw-btn-text';
      zoomResetBtn.style.cssText = 'padding:1px 6px;font-size:9px;';
      zoomResetBtn.title = 'Reset Zoom';
      zoomResetBtn.onclick = () => { sampleZoom = 1; sampleViewport = 0; drawWave(); updateScrollbar(); };
      ctrlRow.appendChild(zoomResetBtn);

      body.appendChild(ctrlRow);

      // Waveform / dropzone
      const waveDiv = document.createElement('div');
      waveDiv.style.cssText = 'position:relative;width:100%;height:140px;border:1px solid var(--c2);';
      body.appendChild(waveDiv);

      // Scrollbar container
      const scrollbarDiv = document.createElement('div');
      scrollbarDiv.style.cssText = 'position:relative;width:100%;height:14px;background:var(--c1);border:1px solid var(--c2);margin-top:-1px;';
      body.appendChild(scrollbarDiv);

      const scrollbarHandle = document.createElement('div');
      scrollbarHandle.style.cssText = 'position:absolute;top:0;height:100%;background:var(--c4);cursor:grab;border-radius:1px;';
      scrollbarDiv.appendChild(scrollbarHandle);

      // Zoom/viewport state (persists during this tab render)
      let sampleZoom = 1;
      let sampleViewport = 0;

      if (pat.audioBuffer) {
         const canvas = document.createElement('canvas');
         canvas.style.cssText = 'width:100%;height:100%;display:block;';
         waveDiv.appendChild(canvas);

         // Loop handle drag state
         const loopDrag = { active: false, which: null }; // 'start' or 'end'

         function drawWave() {
            if (!pat.audioBuffer) return;
            const w = waveDiv.clientWidth || 400;
            const h = 140;
            canvas.width = w;
            canvas.height = h;
            drawSampleWaveform(canvas, pat.audioBuffer, pat.loopStart, pat.loopEnd, pat.loopEnabled, sampleZoom, sampleViewport);
         }

         function updateScrollbar() {
            if (!pat.audioBuffer) return;
            const total = pat.audioBuffer.length;
            const visible = Math.floor(total / sampleZoom);
            const sw = scrollbarDiv.clientWidth || 400;
            if (sampleZoom <= 1) {
               scrollbarHandle.style.left = '0px';
               scrollbarHandle.style.width = sw + 'px';
            } else {
               const handleLeft = (sampleViewport / total) * sw;
               const handleWidth = (visible / total) * sw;
               scrollbarHandle.style.left = handleLeft + 'px';
               scrollbarHandle.style.width = handleWidth + 'px';
            }
         }

         function sampleToX(samplePos) {
            const total = pat.audioBuffer.length;
            const visible = Math.floor(total / sampleZoom);
            const w = waveDiv.clientWidth || 400;
            return ((samplePos - sampleViewport) / visible) * w;
         }

         function xToSample(x) {
            const total = pat.audioBuffer.length;
            const visible = Math.floor(total / sampleZoom);
            const w = waveDiv.clientWidth || 400;
            return Math.floor(sampleViewport + (x / w) * visible);
         }

         requestAnimationFrame(() => { drawWave(); updateScrollbar(); });
         const ro = new ResizeObserver(() => { drawWave(); updateScrollbar(); });
         ro.observe(waveDiv);

         // Mouse wheel zoom
         canvas.onwheel = (e) => {
            e.preventDefault();
            if (!pat.audioBuffer) return;
            const rect = canvas.getBoundingClientRect();
            const mx = e.clientX - rect.left;
            // Zoom centered on mouse position
            const mouseSample = xToSample(mx);
            const delta = e.deltaY < 0 ? 1.3 : 0.77;
            sampleZoom = Math.max(1, Math.min(50, sampleZoom * delta));
            // Adjust viewport to keep mouse position stable
            const total = pat.audioBuffer.length;
            const visible = Math.floor(total / sampleZoom);
            sampleViewport = Math.max(0, Math.min(total - visible, mouseSample - Math.floor(visible / 2)));
            drawWave();
            updateScrollbar();
         };

         // Canvas mouse interaction: drag loop handles or scrub
         canvas.onmousedown = (e) => {
            if (!pat.audioBuffer || !pat.loopEnabled) return;
            const rect = canvas.getBoundingClientRect();
            const mx = e.clientX - rect.left;
            // Check if near a loop handle
            const startHandleX = sampleToX(pat.loopStart);
            const endHandleX = sampleToX(pat.loopEnd);
            const HANDLE_W = 8;
            if (Math.abs(mx - startHandleX) < HANDLE_W) {
               loopDrag.active = true;
               loopDrag.which = 'start';
               canvas.style.cursor = 'ew-resize';
               e.preventDefault();
               return;
            }
            if (Math.abs(mx - endHandleX) < HANDLE_W) {
               loopDrag.active = true;
               loopDrag.which = 'end';
               canvas.style.cursor = 'ew-resize';
               e.preventDefault();
               return;
            }
         };

         canvas.onmousemove = (e) => {
            const rect = canvas.getBoundingClientRect();
            const mx = e.clientX - rect.left;
            if (loopDrag.active) {
               const samplePos = Math.max(0, Math.min(pat.audioBuffer.length, xToSample(mx)));
               if (loopDrag.which === 'start') {
                  pat.loopStart = Math.min(samplePos, pat.loopEnd - 1);
               } else if (loopDrag.which === 'end') {
                  pat.loopEnd = Math.max(samplePos, pat.loopStart + 1);
               }
               drawWave();
               state.onChange();
               return;
            }
            // Update cursor for hover
            if (pat.loopEnabled) {
               const startHandleX = sampleToX(pat.loopStart);
               const endHandleX = sampleToX(pat.loopEnd);
               if (Math.abs(mx - startHandleX) < 8 || Math.abs(mx - endHandleX) < 8) {
                  canvas.style.cursor = 'ew-resize';
               } else {
                  canvas.style.cursor = 'default';
               }
            } else {
               canvas.style.cursor = 'default';
            }
         };

         canvas.onmouseup = () => {
            loopDrag.active = false;
            loopDrag.which = null;
         };
         canvas.onmouseleave = () => {
            loopDrag.active = false;
            loopDrag.which = null;
         };

         // Scrollbar drag
         let sbDrag = { active: false, startX: 0, startViewport: 0 };
         scrollbarHandle.onmousedown = (e) => {
            if (sampleZoom <= 1) return;
            sbDrag.active = true;
            sbDrag.startX = e.clientX;
            sbDrag.startViewport = sampleViewport;
            e.preventDefault();
         };
         document.addEventListener('mousemove', (e) => {
            if (!sbDrag.active || !pat.audioBuffer) return;
            const total = pat.audioBuffer.length;
            const visible = Math.floor(total / sampleZoom);
            const sw = scrollbarDiv.clientWidth || 400;
            const dx = e.clientX - sbDrag.startX;
            const dSamples = (dx / sw) * total;
            sampleViewport = Math.max(0, Math.min(total - visible, sbDrag.startViewport + dSamples));
            drawWave();
            updateScrollbar();
        });
        document.addEventListener('mouseup', () => { sbDrag.active = false; });

         // Loop info
         const loopInfo = document.createElement('div');
         loopInfo.style.cssText = 'font-family:bit,monospace;font-size:9px;color:var(--c6);';
         function updateLoopInfo() {
            const sr = pat.audioBuffer.sampleRate;
            if (pat.loopEnabled && pat.loopEnd > pat.loopStart) {
               const startMs = (pat.loopStart / sr * 1000).toFixed(1);
               const endMs = (pat.loopEnd / sr * 1000).toFixed(1);
               const loopMs = ((pat.loopEnd - pat.loopStart) / sr * 1000).toFixed(1);
               loopInfo.textContent = 'Loop: ' + startMs + 'ms -> ' + endMs + 'ms (' + loopMs + 'ms) | drag handles to adjust';
            } else {
               loopInfo.textContent = 'Loop: off | enable loop checkbox and drag handles on waveform';
            }
         }
         updateLoopInfo();
         body.appendChild(loopInfo);

         // Re-update loop info when loop changes
         const origDrawWave = drawWave;
         drawWave = function() { origDrawWave(); updateLoopInfo(); };

      } else {
         waveDiv.style.cssText += 'display:flex;align-items:center;justify-content:center;border:1px dashed var(--c4);';
         const hint = document.createElement('div');
         hint.style.cssText = 'font-family:bit,monospace;font-size:10px;color:var(--c6);text-align:center;';
         hint.innerHTML = 'Drag & drop a sample here';
         waveDiv.appendChild(hint);
      }

      // Drag & drop
      ['dragenter', 'dragover'].forEach((evt) => {
         waveDiv.addEventListener(evt, (e) => {
            const isBucketItem = e.dataTransfer.types.includes('application/x-bucket-item-id');
            const hasText = e.dataTransfer.types.includes('text/plain');
            if (isBucketItem || hasText) {
               e.preventDefault();
               e.dataTransfer.dropEffect = 'copy';
               waveDiv.style.borderColor = 'var(--c9)';
               waveDiv.style.background = 'var(--c1)';
            }
         });
      });
      waveDiv.addEventListener('dragleave', () => {
         waveDiv.style.borderColor = '';
         waveDiv.style.background = '';
      });
      waveDiv.addEventListener('drop', async (e) => {
         e.preventDefault();
         waveDiv.style.borderColor = '';
         waveDiv.style.background = '';
         if (state.resolveDropItem) {
            const item = await state.resolveDropItem(e.dataTransfer);
            if (item) {
               pat.sampleId = item.id;
               pat.sampleName = item.name;
               pat.audioBuffer = await state.loadBuffer(pat.sampleId);
               pat.loopStart = 0;
               pat.loopEnd = 0;
               state.onChange();
               renderEditor();
               return;
            }
         }
         const itemId = e.dataTransfer.getData('application/x-bucket-item-id') ||
                        e.dataTransfer.getData('text/plain');
         if (itemId && state.getBucketItems) {
            const item = state.getBucketItems().find(it => it.id === itemId);
            if (item) {
               pat.sampleId = item.id;
               pat.sampleName = item.name;
               pat.audioBuffer = await state.loadBuffer(pat.sampleId);
               pat.loopStart = 0;
               pat.loopEnd = 0;
               state.onChange();
               renderEditor();
            }
         }
      });

      editorBody.appendChild(body);
   }

   // =====================================================================
   // ADSR TAB — canvas-based editor with waveform overlay
   // =====================================================================
   function renderADSRTab(pat) {
      const body = document.createElement('div');
      body.style.cssText = 'padding:8px;display:flex;flex-direction:column;gap:8px;overflow:auto;height:100%;';

      if (!pat.adsr) pat.adsr = { attack: 0.01, decay: 0.15, sustain: 0.7, release: 0.2 };

      // Info text
      const info = document.createElement('div');
      info.style.cssText = 'font-family:bit,monospace;font-size:9px;color:var(--c6);';
      info.textContent = 'Drag A handle: attack time | D/S handle: decay time + sustain level | R handle: release time | Sustain line: sustain level only';
      body.appendChild(info);

      // ADSR canvas
      const canvasDiv = document.createElement('div');
      canvasDiv.style.cssText = 'position:relative;width:100%;height:200px;border:1px solid var(--c2);cursor:crosshair;';
      const canvas = document.createElement('canvas');
      canvas.style.cssText = 'width:100%;height:100%;display:block;';
      canvas.width = 600;
      canvas.height = 200;
      canvasDiv.appendChild(canvas);
      body.appendChild(canvasDiv);

      // Value display
      const valDisplay = document.createElement('div');
      valDisplay.style.cssText = 'font-family:bit,monospace;font-size:10px;color:var(--ce);display:flex;gap:12px;';
      body.appendChild(valDisplay);

      // Test button
      const testBtn = document.createElement('button');
      testBtn.textContent = 'Test Envelope';
      testBtn.className = 'lw-btn-text';
      testBtn.onclick = () => {
         const eng = state.getEngine();
         if (!eng || !pat.sampleId) return;
         let buffer = pat.audioBuffer;
         if (!buffer && eng.getMIDISampleBuffer) buffer = eng.getMIDISampleBuffer(pat.sampleId);
         if (!buffer) return;
         const ctx = eng.ctx;
         if (ctx.state === 'suspended') ctx.resume();
         const tmpGain = ctx.createGain();
         tmpGain.gain.value = 0.7;
         tmpGain.connect(ctx.destination);
         const noteDurSec = 0.5;
         const startTime = ctx.currentTime + 0.05;
         if (eng.triggerSampleWithADSR && pat.adsr) {
            eng.triggerSampleWithADSR(buffer, tmpGain, startTime, 0.7, 1.0, 0, 0, noteDurSec, pat.adsr);
         } else {
            eng.triggerSample(buffer, tmpGain, startTime, 0.7, 1.0, 0, 0, noteDurSec);
         }
         const releaseSec = pat.adsr ? pat.adsr.release : 0;
         const totalDur = noteDurSec + releaseSec;
         animateADSRPlayhead(startTime, totalDur);
         setTimeout(() => { try { tmpGain.disconnect(); } catch (e) {} }, (totalDur + 0.3) * 1000);
      };
      body.appendChild(testBtn);

      // ---- ADSR canvas drawing + interaction ----
      // Fixed timeScale: each segment's width is proportional to its time value.
      // The envelope fills the canvas based on actual time values, not stretched to fit.
      const MAX_TIME = 2.0; // max seconds for A+D+R
      const HOLD_TIME = 0.3; // fixed sustain hold duration (visual only)
      let canvasW = 600, canvasH = 200;
      let adsrPlayhead = null;

      function resizeCanvas() {
         canvasW = canvasDiv.clientWidth || 600;
         canvasH = canvasDiv.clientHeight || 200;
         canvas.width = canvasW;
         canvas.height = canvasH;
         drawADSRCanvas();
      }
      requestAnimationFrame(resizeCanvas);
      const ro = new ResizeObserver(() => resizeCanvas());
      ro.observe(canvasDiv);

      function animateADSRPlayhead(startTime, totalDur) {
         if (adsrPlayhead && adsrPlayhead.rafId) cancelAnimationFrame(adsrPlayhead.rafId);
         adsrPlayhead = { startTime, totalDur, rafId: null };
         function tick() {
            if (!adsrPlayhead) return;
            drawADSRCanvas();
            const eng = state.getEngine();
            const now = eng ? eng.ctx.currentTime : performance.now() / 1000;
            if (now - adsrPlayhead.startTime >= adsrPlayhead.totalDur) {
               adsrPlayhead = null;
               drawADSRCanvas();
               return;
            }
            adsrPlayhead.rafId = requestAnimationFrame(tick);
         }
         adsrPlayhead.rafId = requestAnimationFrame(tick);
      }

      // ---- Layout: fixed timeScale, handle positions from time values ----
      function adsrLayout() {
         const pad = 10;
         const drawW = canvasW - pad * 2;
         const drawH = canvasH - pad * 2;
         const top = pad;
         const bottom = canvasH - pad;

         const a = Math.max(0, pat.adsr.attack);
         const d = Math.max(0, pat.adsr.decay);
         const s = Math.max(0, Math.min(1, pat.adsr.sustain));
         const r = Math.max(0, pat.adsr.release);
         const totalTime = a + d + HOLD_TIME + r;
         // Fixed timeScale: MAX_TIME maps to full drawW
         const timeScale = drawW / MAX_TIME;

         const ax = pad + a * timeScale;           // attack peak
         const dx = ax + d * timeScale;             // decay end = sustain start
         const sx = dx + HOLD_TIME * timeScale;     // sustain end = release start
         const rx = sx + r * timeScale;             // release end
         const susY = bottom - s * drawH;
         const peakY = top;

         return { pad, drawW, drawH, top, bottom, timeScale, ax, dx, sx, rx, susY, peakY, a, d, s, r };
      }

      function drawADSRCanvas() {
         const ctx = canvas.getContext('2d');
         const col = colors();
         const w = canvasW, h = canvasH;
         const L = adsrLayout();
         const pad = L.pad, drawW = L.drawW, drawH = L.drawH;
         const top = L.top, bottom = L.bottom;
         const ax = L.ax, dx = L.dx, sx = L.sx, rx = L.rx;
         const susY = L.susY, peakY = L.peakY;

         ctx.fillStyle = col.bg;
         ctx.fillRect(0, 0, w, h);

         // ---- Background waveform ----
         if (pat.audioBuffer) {
            const buf = pat.audioBuffer;
            const numCh = buf.numberOfChannels;
            const totalSamples = buf.length;
            const samplesPerPixel = totalSamples / drawW;
            ctx.strokeStyle = col.grid;
            ctx.lineWidth = 1;
            ctx.globalAlpha = 0.4;
            ctx.beginPath();
            const midY = (top + bottom) / 2;
            for (let x = 0; x < drawW; x++) {
               const startS = Math.floor(x * samplesPerPixel);
               const endS = Math.floor((x + 1) * samplesPerPixel);
               let max = 0;
               for (let ch2 = 0; ch2 < numCh; ch2++) {
                  const data = buf.getChannelData(ch2);
                  for (let i = startS; i < endS && i < totalSamples; i += 64) {
                     const v = Math.abs(data[i]);
                     if (v > max) max = v;
                  }
               }
               const y = max * drawH * 0.45;
               ctx.moveTo(pad + x + 0.5, midY - y);
               ctx.lineTo(pad + x + 0.5, midY + y);
            }
            ctx.stroke();
            ctx.globalAlpha = 1;
         }

         // ---- Grid ----
         ctx.strokeStyle = col.grid;
         ctx.lineWidth = 0.5;
         for (let i = 0; i <= 4; i++) {
            const x = pad + (i / 4) * drawW;
            ctx.beginPath(); ctx.moveTo(x, top); ctx.lineTo(x, bottom); ctx.stroke();
            const y = top + (i / 4) * drawH;
            ctx.beginPath(); ctx.moveTo(pad, y); ctx.lineTo(w - pad, y); ctx.stroke();
         }

         // ---- Fill under envelope ----
         ctx.fillStyle = col.note;
         ctx.globalAlpha = 0.12;
         ctx.beginPath();
         ctx.moveTo(pad, bottom);
         ctx.lineTo(ax, peakY);
         ctx.lineTo(dx, susY);
         ctx.lineTo(sx, susY);
         ctx.lineTo(rx, bottom);
         ctx.lineTo(pad, bottom);
         ctx.fill();
         ctx.globalAlpha = 1;

         // ---- Envelope line ----
         ctx.strokeStyle = col.accent;
         ctx.lineWidth = 2;
         ctx.beginPath();
         ctx.moveTo(pad, bottom);
         ctx.lineTo(ax, peakY);
         ctx.lineTo(dx, susY);
         ctx.lineTo(sx, susY);
         ctx.lineTo(rx, bottom);
         ctx.stroke();

         // ---- Draggable handles ----
         // 3 handles: Attack (peak), Decay/Sustain (sustain level + decay time), Release (release end)
         const handles = [
            { x: ax, y: peakY, label: 'A', param: 'attack' },
            { x: dx, y: susY, label: 'D/S', param: 'decay' },
            { x: rx, y: bottom, label: 'R', param: 'release' },
         ];
         for (const hd of handles) {
            // Outer circle
            ctx.fillStyle = col.accent;
            ctx.beginPath();
            ctx.arc(hd.x, hd.y, 6, 0, Math.PI * 2);
            ctx.fill();
            // Inner circle
            ctx.fillStyle = col.bg;
            ctx.beginPath();
            ctx.arc(hd.x, hd.y, 3, 0, Math.PI * 2);
            ctx.fill();
            // Label
            ctx.fillStyle = col.textDim;
            ctx.font = '9px monospace';
            ctx.textAlign = 'center';
            ctx.fillText(hd.label, hd.x, hd.y - 10);
         }

         // ---- Sustain level dashed line (visual reference) ----
         ctx.strokeStyle = col.textDim;
         ctx.lineWidth = 0.5;
         ctx.setLineDash([3, 3]);
         ctx.globalAlpha = 0.4;
         ctx.beginPath();
         ctx.moveTo(pad, susY);
         ctx.lineTo(sx, susY);
         ctx.stroke();
         ctx.setLineDash([]);
         ctx.globalAlpha = 1;

         // ---- Value display ----
         valDisplay.innerHTML = '';
         const vals = [
            { label: 'A', val: pat.adsr.attack.toFixed(3) + 's' },
            { label: 'D', val: pat.adsr.decay.toFixed(3) + 's' },
            { label: 'S', val: pat.adsr.sustain.toFixed(2) },
            { label: 'R', val: pat.adsr.release.toFixed(3) + 's' },
         ];
         for (const v of vals) {
            const span = document.createElement('span');
            span.textContent = v.label + ': ' + v.val;
            valDisplay.appendChild(span);
         }

         // ---- Playhead ----
         if (adsrPlayhead) {
            const eng = state.getEngine();
            const now = eng ? eng.ctx.currentTime : performance.now() / 1000;
            const elapsed = now - adsrPlayhead.startTime;
            if (elapsed >= 0 && elapsed <= adsrPlayhead.totalDur) {
               const a2 = pat.adsr.attack;
               const d2 = pat.adsr.decay;
               const r2 = pat.adsr.release;
               const totalDrawTime = a2 + d2 + HOLD_TIME + r2;
               const px = pad + elapsed * L.timeScale;
               ctx.strokeStyle = col.text;
               ctx.lineWidth = 1.5;
               ctx.globalAlpha = 0.8;
               ctx.beginPath();
               ctx.moveTo(px, top);
               ctx.lineTo(px, bottom);
               ctx.stroke();
               ctx.globalAlpha = 1;
               // Dot on envelope
               const s2 = L.s;
               const susY2 = L.susY;
               let envY;
               if (elapsed < a2) {
                  envY = bottom + (peakY - bottom) * (elapsed / Math.max(a2, 0.001));
               } else if (elapsed < a2 + d2) {
                  const t = (elapsed - a2) / Math.max(d2, 0.001);
                  envY = peakY + t * (susY2 - peakY);
               } else if (elapsed < a2 + d2 + HOLD_TIME) {
                  envY = susY2;
               } else {
                  const t = (elapsed - a2 - d2 - HOLD_TIME) / Math.max(r2, 0.001);
                  envY = susY2 + t * (bottom - susY2);
               }
               ctx.fillStyle = col.text;
               ctx.beginPath();
               ctx.arc(px, envY, 4, 0, Math.PI * 2);
               ctx.fill();
            }
         }
      }

      // ---- Hit test: which handle or region is the mouse near? ----
      function hitTestADSR(mx, my) {
         const L = adsrLayout();
         const handles = [
            { x: L.ax, y: L.peakY, param: 'attack' },
            { x: L.dx, y: L.susY, param: 'decay' },
            { x: L.rx, y: L.bottom, param: 'release' },
         ];
         // Check handles first (within 12px radius)
         for (const h of handles) {
            if (Math.hypot(mx - h.x, my - h.y) < 12) return h.param;
         }
         // Then check if near the sustain line (for sustain-only drag)
         if (mx > L.ax && mx < L.sx && Math.abs(my - L.susY) < 8) return 'sustain';
         return null;
      }

      canvas.onmousedown = (e) => {
         const rect = canvas.getBoundingClientRect();
         const mx = e.clientX - rect.left;
         const my = e.clientY - rect.top;
         const param = hitTestADSR(mx, my);
         if (!param) return;
         adsrDrag.active = true;
         adsrDrag.param = param;
         updateADSRFromMouse(mx, my);
         e.preventDefault();
      };

      canvas.onmousemove = (e) => {
         const rect = canvas.getBoundingClientRect();
         const mx = e.clientX - rect.left;
         const my = e.clientY - rect.top;
         if (!adsrDrag.active) {
            const param = hitTestADSR(mx, my);
            if (param === 'decay') canvas.style.cursor = 'move';
            else if (param === 'sustain') canvas.style.cursor = 'ns-resize';
            else if (param) canvas.style.cursor = 'ew-resize';
            else canvas.style.cursor = 'default';
            return;
         }
         updateADSRFromMouse(mx, my);
      };

      function updateADSRFromMouse(mx, my) {
         const L = adsrLayout();
         const pad = L.pad;
         const drawH = L.drawH;
         const top = L.top;
         const ts = L.timeScale;

         if (adsrDrag.param === 'attack') {
            // X controls attack time (0 to just before decay handle)
            const maxA = L.d > 0 ? MAX_TIME - L.d * 0.1 : MAX_TIME;
            pat.adsr.attack = Math.max(0.001, Math.min(maxA, (mx - pad) / ts));
         } else if (adsrDrag.param === 'decay') {
            // X controls decay time (relative to attack end)
            const relX = mx - L.ax;
            pat.adsr.decay = Math.max(0, Math.min(MAX_TIME, relX / ts));
            // Y controls sustain level
            const sVal = 1 - (my - top) / drawH;
            pat.adsr.sustain = Math.max(0, Math.min(1, sVal));
         } else if (adsrDrag.param === 'sustain') {
            // Y only controls sustain level
            const sVal = 1 - (my - top) / drawH;
            pat.adsr.sustain = Math.max(0, Math.min(1, sVal));
         } else if (adsrDrag.param === 'release') {
            // X controls release time (relative to sustain end)
            const relX = mx - L.sx;
            pat.adsr.release = Math.max(0.001, Math.min(MAX_TIME, relX / ts));
         }
         drawADSRCanvas();
         state.onChange();
      }

      function endADSRDrag() {
         adsrDrag.active = false;
         adsrDrag.param = null;
      }

      canvas.onmouseup = endADSRDrag;
      canvas.onmouseleave = endADSRDrag;
      document.addEventListener('mouseup', endADSRDrag);

      editorBody.appendChild(body);
   }

   // ---- Draw waveform with loop markers ----
   function drawSampleWaveform(canvas, audioBuffer, loopStart, loopEnd, loopEnabled, zoom, viewport) {
      if (!audioBuffer) return;
      zoom = zoom || 1;
      viewport = viewport || 0;
      const ctx = canvas.getContext('2d');
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      const numChannels = audioBuffer.numberOfChannels;
      const totalSamples = audioBuffer.length;
      const visibleSamples = Math.floor(totalSamples / zoom);
      const samplesPerPixel = visibleSamples / w;

      const c0 = getComputedStyle(document.documentElement).getPropertyValue('--c0') || '#111';
      const c9 = getComputedStyle(document.documentElement).getPropertyValue('--c9') || '#4a9';
      const cb = getComputedStyle(document.documentElement).getPropertyValue('--cb') || '#6b8';
      const cf = getComputedStyle(document.documentElement).getPropertyValue('--cf') || '#fff';

      ctx.fillStyle = c0.trim();
      ctx.fillRect(0, 0, w, h);

      // Draw waveform for visible region only
      ctx.strokeStyle = c9.trim();
      ctx.lineWidth = 1;
      ctx.beginPath();
      const midY = h / 2;
      for (let x = 0; x < w; x++) {
         const startSample = Math.floor(viewport + x * samplesPerPixel);
         const endSample = Math.floor(viewport + (x + 1) * samplesPerPixel);
         if (startSample >= totalSamples) break;
         let max = 0;
         for (let ch = 0; ch < numChannels; ch++) {
            const data = audioBuffer.getChannelData(ch);
            for (let i = startSample; i < endSample && i < totalSamples; i += Math.max(1, Math.floor(samplesPerPixel / 64))) {
               const v = Math.abs(data[i]);
               if (v > max) max = v;
            }
         }
         const y = max * midY * 0.9;
         ctx.moveTo(x + 0.5, midY - y);
         ctx.lineTo(x + 0.5, midY + y);
      }
      ctx.stroke();

      // Draw loop region and handles only if loop is enabled
      if (loopEnabled && loopEnd > loopStart) {
         const startX = ((loopStart - viewport) / visibleSamples) * w;
         const endX = ((loopEnd - viewport) / visibleSamples) * w;

         // Highlight loop region
         ctx.fillStyle = cb.trim();
         ctx.globalAlpha = 0.12;
         ctx.fillRect(startX, 0, endX - startX, h);
         ctx.globalAlpha = 1;

         // Loop start handle (vertical line + triangle)
         ctx.strokeStyle = cb.trim();
         ctx.lineWidth = 2;
         ctx.beginPath(); ctx.moveTo(startX, 0); ctx.lineTo(startX, h); ctx.stroke();
         ctx.fillStyle = cb.trim();
         ctx.beginPath();
         ctx.moveTo(startX, 0);
         ctx.lineTo(startX + 8, 0);
         ctx.lineTo(startX, 8);
         ctx.fill();

         // Loop end handle (vertical line + triangle)
         ctx.beginPath(); ctx.moveTo(endX, 0); ctx.lineTo(endX, h); ctx.stroke();
         ctx.fillStyle = cb.trim();
         ctx.beginPath();
         ctx.moveTo(endX, 0);
         ctx.lineTo(endX - 8, 0);
         ctx.lineTo(endX, 8);
         ctx.fill();

         // Labels
         ctx.fillStyle = cf.trim();
         ctx.font = '8px monospace';
         ctx.textAlign = 'left';
         ctx.fillText('L', startX + 2, h - 4);
         ctx.textAlign = 'right';
         ctx.fillText('R', endX - 2, h - 4);
      }
   }

   // ---- Preview player ----
   function startPreview() {
      const pat = state.patterns[state.activePatternIdx];
      if (!pat || !pat.sampleId || pat.notes.length === 0) return;
      const eng = state.getEngine();
      if (!eng) return;

      stopPreview();
      if (eng.state.isPlaying) eng.stop();

      state.previewPlaying = true;
      state.previewCurrentStep = 0;
      const ctx = eng.ctx;
      if (ctx.state === 'suspended') ctx.resume();

      const secPerBeat = 60 / (pat.bpm || parseFloat(document.getElementById('lw-bpm') && document.getElementById('lw-bpm').value) || 120);
      const secPerStep = secPerBeat / STEPS_PER_BEAT;
      state.previewNextStepTime = ctx.currentTime + 0.2;
      state.previewStartTime = state.previewNextStepTime;

      const previewGain = ctx.createGain();
      previewGain.gain.value = 0.7;
      previewGain.connect(ctx.destination);
      state.previewGain = previewGain;

      if (eng.getMIDISampleBuffer) eng.getMIDISampleBuffer(pat.sampleId);

      function scheduler() {
         if (!state.previewPlaying) return;
         try {
            const lookAhead = 0.2;
            while (state.previewNextStepTime < ctx.currentTime + lookAhead) {
               const step = state.previewCurrentStep;
               const whenSec = state.previewNextStepTime;
               for (const note of pat.notes) {
                  if (note.startStep === step) {
                     let buffer = pat.audioBuffer;
                     if (!buffer && eng.getMIDISampleBuffer) buffer = eng.getMIDISampleBuffer(pat.sampleId);
                     if (buffer) {
                        const playbackRate = semitoneToPlaybackRate(note.midi);
                        const noteDurSec = (note.lengthSteps / STEPS_PER_BEAT) * secPerBeat;
                        const velocity = (note.velocity !== undefined ? note.velocity : 1.0) * 0.7;
                        const loopOn = pat.loopEnabled && pat.loopEnd > pat.loopStart;
                        if (loopOn && eng.triggerSampleLooped) {
                           eng.triggerSampleLooped(buffer, previewGain, whenSec, velocity, playbackRate, 0, noteDurSec, pat.adsr, true, pat.loopStart, pat.loopEnd);
                        } else if (eng.triggerSampleWithADSR && pat.adsr) {
                           eng.triggerSampleWithADSR(buffer, previewGain, whenSec, velocity, playbackRate, 0, 0, noteDurSec, pat.adsr);
                        } else {
                           eng.triggerSample(buffer, previewGain, whenSec, velocity, playbackRate, 0, 0, noteDurSec);
                        }
                     }
                  }
               }
               state.previewCurrentStep = (state.previewCurrentStep + 1) % (pat.beats * STEPS_PER_BEAT);
               state.previewNextStepTime += secPerStep;
            }
         } catch (e) {}
         state.previewSchedulerId = setTimeout(scheduler, 25);
      }
      scheduler();
      updatePreviewButton();

      // Start playhead animation
      if (pat._animatePlayhead) pat._animatePlayhead();
   }

   function stopPreview() {
      state.previewPlaying = false;
      if (state.previewSchedulerId) {
         clearTimeout(state.previewSchedulerId);
         state.previewSchedulerId = null;
      }
      if (state.previewPlayheadRAF) {
         cancelAnimationFrame(state.previewPlayheadRAF);
         state.previewPlayheadRAF = null;
      }
      updatePreviewButton();
      // Redraw to clear playhead
      const pat = state.patterns[state.activePatternIdx];
      if (pat && pat._drawGrid) pat._drawGrid();
   }

   function togglePreview() {
      if (state.previewPlaying) stopPreview();
      else startPreview();
   }

   function updatePreviewButton() {
      const btn = document.getElementById('lw-pianoroll-preview-btn');
      if (btn) {
         btn.textContent = state.previewPlaying ? '\uE801' : '\uE800';
         btn.style.color = state.previewPlaying ? 'var(--c6)' : 'var(--c9)';
      }
   }

   // ---- Public API ----
   function getActivePattern() {
      return state.patterns[state.activePatternIdx] || null;
   }

   function getPatterns() {
      return state.patterns.map(p => {
         const { audioBuffer, _drawGrid, _animatePlayhead, ...rest } = p;
         return rest;
      });
   }

   // Get live pattern object by index (reference, not copy)
   function getPatternRef(idx) {
      return state.patterns[idx] || null;
   }

   function loadPatterns(pats) {
      state.patterns = pats || [];
      // Ensure all patterns have UIDs (for live-reference linking on load)
      for (const pat of state.patterns) {
         if (!pat.uid) {
            pat.uid = 'pat_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7);
         }
      }
      state.activePatternIdx = 0;
      renderList();
      renderEditor();
   }

   // ---- Initialize ----
   if (state.patterns.length === 0) {
      createNewPattern();
   } else {
      renderList();
      renderEditor();
   }

   return {
      getActivePattern,
      getPatterns,
      getPatternRef,
      loadPatterns,
      render: () => { renderList(); renderEditor(); },
   };
}

module.exports = { createPianoRoll, semitoneToPlaybackRate, midiToNoteName, NOTE_NAMES };
